/* eslint-disable no-nested-ternary,import/no-unresolved */
/* eslint-disable no-undef */
/* eslint-disable react/forbid-prop-types */
import BigNumber from 'bignumber.js';
import moment from 'moment';
import React from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { isBrowser, isMobile } from 'react-device-detect';
import { MAIN_ICON_COLORS } from 'assets/components';
import { TRANSACTION_TYPE } from '../../enums';
import { currencies } from '../../../../utilities/constants';
import { formatNumber } from '../../../../utilities/formatHelpers';
import ModalBodyLayout from '../../../shared/ModalBodyLayout';
import { copyToClipboard, generateExternalTransactionURL } from '../../../../utilities/helpers';
import { useCurrency } from '../../../../utilities/sharedHooks/settings';
import { useUserCardProvider } from '../../../../utilities/sharedHooks/user';
import { getCardCapabilities } from '../../../Card/constants';
import Icon from '../../../../components/Icon';
import { ICONS } from '../../../../components/Icon/constants';

const Details = ({ transaction, wallet }) => {
  const { t: translate } = useTranslation();
  const transactionDate = moment(transaction.transaction_date).format('LLL');
  const walletCoin = wallet.coin.toUpperCase();
  const walletCurrency = useCurrency(walletCoin);
  const currency = currencies[walletCoin];
  const recipient = transaction?.destination_address || wallet.address;
  const { cardProvider } = useUserCardProvider();

  const amount = transaction.base_amount * currency.conversion;
  const internalFees = transaction.internal_fee * currency.conversion;
  let fees = transaction.transaction_fee * currency.conversion;
  fees += internalFees;
  const total = new BigNumber(fees).plus(amount).toNumber();
  const renderTitle = () => (
    <div className={`w-100 ${isBrowser ? 'flex-row-center' : ''}`}>
      <b className="text-f5 text-dark me-auto">{translate('transactions_details_title')}</b>
      <p className={`text-shadow text-f6 ${isMobile ? 'mt-4' : 'float-right'}`}>
        {transactionDate}
      </p>
    </div>
  );

  const isExternalTrx = transaction.bitgo_tx_id && transaction.bitgo_tx_id !== '0';
  const TxID = isExternalTrx ? transaction.bitgo_tx_id : transaction.id;
  const handleClickOnTxID = () => {
    const _currency = walletCurrency.is_erc20_token ? 'eth' : walletCoin;
    const transactionUrl = generateExternalTransactionURL(_currency, transaction.bitgo_tx_id);
    return transactionUrl;
  };

  const isAddFunds =
    transaction.transaction_type === TRANSACTION_TYPE.RECEIVE ||
    transaction.source_address !== wallet?.address;

  const isFeesAndTotalHidded =
    transaction.transaction_type === TRANSACTION_TYPE.RECEIVE ||
    (transaction.transaction_type === TRANSACTION_TYPE.SEND &&
      transaction?.destination_address === wallet?.address);

  const isBufferSendTransaction =
    transaction.transaction_type === TRANSACTION_TYPE.SEND &&
    transaction.source_address === wallet?.address &&
    transaction.destination_address?.toLowerCase() === 'buffer';

  const isRefundOrCreditTransaction =
    transaction.transaction_type === TRANSACTION_TYPE.RECEIVE &&
    transaction?.acceptor_name_location !== null;

  return (
    <ModalBodyLayout renderTitle={renderTitle}>
      <div className="bg-light rounded-3 mt-4">
        <div className="flex-row-center p-3">
          <p className="text-f6 text-shadow">{translate('transactions_details_status')}</p>
          <p className="capitalize text-bold text-f6">{transaction.transaction_state}</p>
        </div>
        <hr className="m-0 text-disabled" />
        <div className="flex-row-center p-3">
          <p className="text-f6 text-shadow">{translate('transactions_details_type')}</p>
          <p className="capitalize text-bold text-right text-f6">
            {isBufferSendTransaction && translate('transaction_type_card_funding_title')}
            {isAddFunds &&
              !isRefundOrCreditTransaction &&
              translate(`transaction_type_${TRANSACTION_TYPE.RECEIVE}_title`)}
            {isRefundOrCreditTransaction &&
              translate(`transaction_type_${TRANSACTION_TYPE.REFUND}_title`)}
            {!isBufferSendTransaction &&
              !isAddFunds &&
              !isRefundOrCreditTransaction &&
              translate(`transaction_type_${transaction.transaction_type}_title`)}
          </p>
        </div>

        {isBufferSendTransaction || isRefundOrCreditTransaction ? (
          <>
            <hr className="m-0 text-disabled" />
            <div className="flex-row-center p-3">
              <p className="text-f6 text-shadow">{translate('transaction_details_merchant')}</p>
              <p className="text-bold text-right text-f6">
                {getCardCapabilities(cardProvider).truncatesMerchantLocation
                  ? transaction.acceptor_name_location?.split(',')[0]
                  : transaction.acceptor_name_location}
              </p>
            </div>
          </>
        ) : null}
        <hr className="m-0 text-disabled" />
        <div className="flex-row-center p-3">
          <p className="text-f6 text-shadow">{translate('transactions_details_amount')}</p>
          <p className="text-bold text-f6">{`${formatNumber(
            amount,
            currency.decimal
          )} ${walletCoin}`}</p>
        </div>
        {!isFeesAndTotalHidded && !isBufferSendTransaction && (
          <>
            <hr className="m-0 text-disabled" />
            <div className="flex-row-center p-3">
              <p className="text-f6 text-shadow">{translate('transactions_details_fees')}</p>
              <p className="text-bold text-f6">{`${formatNumber(
                fees,
                currency.decimal
              )} ${walletCoin}`}</p>
            </div>
          </>
        )}
      </div>
      {!isFeesAndTotalHidded && (
        <div className="bg-light rounded-3 mt-4">
          <div className="flex-row-center p-3">
            <p className="text-f6 text-shadow">{translate('transactions_details_total')}</p>
            <p className="text-bold text-f6">{`${formatNumber(
              total,
              currency.decimal
            )} ${walletCoin}`}</p>
          </div>
        </div>
      )}
      <div className="mt-5">
        <div>
          <p className="text-f6 text-shadow">{translate('transactions_details_recipient')}</p>
          <p className="text-f6">{recipient}</p>
        </div>
        <div className="mt-3">
          <p className="text-f6 text-shadow">
            {translate('transactions_details_txid')}{' '}
            <i
              onKeyDown={null}
              tabIndex={-1}
              role="button"
              aria-label="Copy"
              className="icon-copy-other text-primary text-f5 cursor-pointer"
              onClick={() => copyToClipboard(TxID)}
            />
          </p>
          <p className="text-f6 text-break">{TxID}</p>
          {isExternalTrx && (
            <div onKeyDown={null} className="d-flex flex-row mt-2 cursor-pointer">
              <a
                target="_blank"
                className="text-decoration-none d-flex flex-row"
                href={handleClickOnTxID() || '#'}
                download={handleClickOnTxID()}
                rel="noreferrer">
                <p className="text-f5 text-primary">
                  {translate('transactions_details_explorer_btn_label')}
                </p>
                <div className="ms-2" style={{ width: '1.2rem', height: '1.2rem' }}>
                  <Icon icon={ICONS.REDIRECT_MARK} color={MAIN_ICON_COLORS.PRIMARY} />
                </div>
              </a>
            </div>
          )}
        </div>
      </div>
    </ModalBodyLayout>
  );
};

export default Details;

Details.propTypes = {
  transaction: PropTypes.object.isRequired,
  wallet: PropTypes.object.isRequired,
};
